import * as React from 'react';
import { AuthContext } from '../../contextProviders/AuthContext';
import { Navigate } from 'react-router-dom';
import styled from 'styled-components';
import NeveLogo from 'assets/icons/NeveLogo';
import { ConfirmSignUpForm } from './ConfirmSignUpForm';
import { SignUpForm } from './SignUpForm';
import { SignInForm } from './SignInForm';
import { ResetPasswordForm } from './ResetPasswordForm';
import ConfirmPasswordResetForm from './ConfirmResetPasswordForm';
import { FlexColSm } from 'components/Elements';

export type HomepageState = 'signIn' | 'signUp' | 'confirmSignUp' | 'resetPassword' | 'confirmResetPassword';
export const EmailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const PasswordPattern = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^\w\s]).{8,}$/;

export type SharedData = {
    emailAddress: string;
    password: string;
};

type HomepageContextState = {
    state: HomepageState;
    setState: React.Dispatch<React.SetStateAction<HomepageState>>;
    sharedData: SharedData;
    setSharedData: React.Dispatch<React.SetStateAction<SharedData>>;
};

export const HomepageContext = React.createContext<HomepageContextState>({
    state: 'signIn',
    setState: () => {},
    sharedData: { emailAddress: '', password: '' },
    setSharedData: () => {},
});

export function HomepageProvider({
    children,
    homepageState,
}: {
    children: React.ReactNode | React.ReactNode[];
    homepageState: HomepageState;
}): JSX.Element {
    const [state, setState] = React.useState<HomepageState>(homepageState);
    const [sharedData, setSharedData] = React.useState<SharedData>({
        emailAddress: '',
        password: '',
    });

    const contextState: HomepageContextState = {
        state: state,
        setState: setState,
        sharedData: sharedData,
        setSharedData: setSharedData,
    };
    return <HomepageContext.Provider value={contextState}>{children}</HomepageContext.Provider>;
}

type HomepageProps = {
    homepageState: HomepageState;
};

export default function Homepage({ homepageState }: HomepageProps): JSX.Element {
    return (
        <HomepageProvider homepageState={homepageState}>
            <HomepageContent />
        </HomepageProvider>
    );
}

function HomepageContent(): JSX.Element {
    const { isAuthenticated } = React.useContext(AuthContext);
    const { state } = React.useContext(HomepageContext);

    if (isAuthenticated) {
        return <Navigate to="/" replace />;
    }

    let body = <></>;

    switch (state) {
        case 'signUp':
            body = <SignUpForm />;
            break;
        case 'confirmSignUp':
            body = <ConfirmSignUpForm />;
            break;
        case 'resetPassword':
            body = <ResetPasswordForm />;
            break;
        case 'confirmResetPassword':
            body = <ConfirmPasswordResetForm />;
            break;
        case 'signIn':
        default:
            body = <SignInForm />;
            break;
    }

    return <HomepageBase>{body}</HomepageBase>;
}

type HomepageBaseProps = {
    children: React.ReactNode;
};

function HomepageBase({ children }: HomepageBaseProps): JSX.Element {
    return (
        <Container>
            <NeveLogo />
            {children}
        </Container>
    );
}

const Container = styled(FlexColSm)`
    align-items: center;
    margin: 40px 0;
`;
